import type { TemplateCalculatedItem, TemplateMeasurementContext } from "./template-quote-calculations";
import { prepareBathroomTemplateScope } from "./bathroom-template-scope";

/**
 * Scope references, NOT a price list. Rates, personal overrides and recipes
 * always come from the effective Cost Library. Keep these names separate from
 * the builder-facing titles; changing a display title must not change pricing.
 */
const REFERENCES: Record<string, string[]> = {
  "Preliminaries & Site Setup": ["Preliminaries and site setup", "Site set up and establishment"],
  "Scaffolding": ["Scaffolding erect and dismantle (domestic)"],
  "Skip Hire": ["Skip hire (6-8 yard builder's skip)", "Waste Removal Skips"],
  "Plant & Equipment": ["Plant and equipment"],
  "Temporary Works": ["Temporary works"],
  "Groundworks": ["Extension Groundworks"],
  "Strip Footings": ["Strip foundations"],
  // Never bind the floor-only scope to "Concrete Base": that library package
  // includes foundations which are already separate template lines.
  "Concrete Base": ["Ground floor concrete slab"],
  "Damp Proof Course": ["Damp Proof Course"],
  "Brickwork": ["Cavity wall construction"],
  "Cavity Closers": ["Cavity Closer Installation"],
  "Cavity Trays": ["Cavity Tray Installation"],
  "Beam Installation": ["Steel beam (RSJ/UB) supply and install"],
  "Steel Ridge Beam": ["Steel beam (RSJ/UB) supply and install"],
  "Padstones": ["Padstones"],
  "Lintel Installation": ["Catnic lintel install", "Steel lintel supply and fit"],
  "Cavity Lintel": ["Catnic lintel install", "Steel lintel supply and fit"],
  "Window Installation": ["Supply and fit uPVC window"],
  "Dormer Window": ["Supply and fit aluminium window"],
  "Internal Door": ["Hang internal door"],
  "Internal Doors": ["Hang internal door"],
  "Internal Door at Loft Level": ["Fire door supply and fit (FD30)"],
  "Making Good": ["Making Good (General)", "Making good after structural works"],
  "Complete Roof": ["Complete Roof"],
  "Rainwater Goods": ["Guttering and downpipes"],
  "Dot & Dab Plasterboard": ["Dot and dab plasterboard"],
  "Ceiling Plasterboard": ["Plasterboard ceiling"],
  "Plaster Skim Coat": ["Skim coat and plastering"],
  "T&G Chipboard Flooring": ["T&G chipboard flooring"],
  "T&G Chipboard Flooring (GF)": ["T&G chipboard flooring"],
  "Flooring": ["Laminate flooring"],
  "Flooring Throughout": ["Laminate flooring"],
  "Skirting Boards": ["Install skirting board"],
  "Architraves": ["Install architrave"],
  "Window Boards": ["Install window board"],
  "Window Board": ["Install window board"],
  "Wall Tiling": ["Wall tiling (standard ceramic)"],
  "Floor Tiling": ["Floor tiling (porcelain)"],
  "WC Tiling": ["Floor tiling (porcelain)"],
  "Sanitaryware (Labour Only)": ["Sanitaryware Installation (Labour Only)"],
  "Decorating": ["Mist coat and two coats emulsion (walls & ceiling)"],
  "Full Redecoration": ["Mist coat and two coats emulsion (walls & ceiling)"],
  "First Floor Joists": ["Suspended timber floor construction"],
  "Loft Floor Joists": ["Suspended timber floor construction"],
  "New Staircase": ["Standard softwood staircase"],
  "New Loft Staircase": ["Loft conversion staircase"],
  "Building Regulations Application": ["Building control fees"],
  "Building Regulations Notice": ["Building control fees"],
  "Plasterboard & Skim": ["Plasterboard and skim to walls"],
  "Internal Lining & Skim": ["Plasterboard and skim to walls"],
  "Velux Rooflights": ["Install rooflight / Velux window"],
  "Roof Insulation": ["Rafter insulation between and below"],
  "Bathroom Strip Out": ["Strip out bathroom"],
  "Kitchen Strip Out": ["Strip out kitchen"],
  "Bath (Labour Only)": ["Install bath and taps"],
  "Kitchen Unit Installation (Labour Only)": ["Kitchen fitting (standard)"],
  "Worktop Installation (Labour Only)": ["Worktop installation (laminate)"],
  "Splashback Tiling": ["Splashback tiling"],
  "Structural Opening": ["Form structural opening in wall"],
  "Strip Existing Roof": ["Strip roof covering"],
  "Pitched Roof - Concrete Tile": ["Concrete interlocking roof tiles"],
  "Pitched Roof - Natural Slate": ["Natural slate roof tiles"],
  "Flat Roof - GRP Fibreglass": ["GRP fibreglass flat roof"],
  "Flat Roof - EPDM": ["EPDM flat roof covering"],
  "Lead Flashings & Valleys": ["Lead flashing to abutment"],
  "Fascia & Soffit Replacement": ["Install fascia and soffit boards"],
  "Guttering Replacement": ["Guttering and downpipes"],
  "Paving (Indian Sandstone)": ["Indian sandstone patio"],
  "Lawn - Turf": ["Turfing"],
  "Composite Decking": ["Composite decking"],
  "Timber Fencing": ["Close board fencing 1.8m"],
  "Full Rewire": ["Full house rewire (3 bed)"],
  "Heating Extension": ["Install radiator"],
  "Heating Connection": ["Install radiator"],
  "Plumbing Work": ["Pipework Alterations"],
  "Electrical Work": ["Electrical Work"],
  "Electrical Works": ["Electrical Works"],
  "Electrical Installation": ["Electrical Installation"],
  "Electrical Supply Run": ["Electrical Supply Run"],
  "Roof Structure Strengthening": ["Carpentry first fix"],
  "Structural Works": ["Structural Works"],
  "Strip Out": ["Strip Out"],
  "Full Internal Strip Out": ["Full Internal Strip Out"],
  "Dormer Structure": ["Dormer Structure"],
  "New Gable Wall": ["New Gable Wall"],
  "Roof Alteration & Re-tile": ["Roof Alteration & Re-tile"],
  "Full Replumb": ["Full Replumb"],
  "Electrical Update": ["Electrical Update"],
  "Heating System Replacement": ["Heating System Replacement"],
  "Kitchen Renovation": ["Kitchen Renovation"],
  "Permitted Development & Building Regulations Check": ["Building control fees"],
};

export const normaliseLibraryName = (name: string) =>
  name.toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, " ").trim();

/** Apply unit/scope corrections BEFORE deriving quantities, never to prices. */
export function prepareTemplateLibraryScope<T extends TemplateCalculatedItem>(
  seeds: T[], ctx: TemplateMeasurementContext,
): T[] {
  // UK scope references must never select a different country's products.
  // Other markets retain their existing localised server-matching path.
  if (ctx.market && ctx.market !== "uk") return seeds;
  const number = (key: string) => ctx.values[key]?.trim() ? Number(ctx.values[key]) : undefined;
  const floorArea = number("floorArea") ?? (ctx.projectType === "bathroom_refurb"
    ? seeds.find(i => i.name === "Floor Tiling")?.quantity
    : ctx.width && ctx.depth ? ctx.width * ctx.depth : undefined);
  const foundationRun = number("foundationLength") ?? (
    ["extension", "double_storey_extension"].includes(ctx.projectType) && ctx.width && ctx.depth
      ? ctx.width + 2 * ctx.depth : undefined);
  const line = (seed: T, name: string, target: string, unit: string, quantity: number): T => ({
    ...seed, id: `${seed.id}-${normaliseLibraryName(target).replaceAll(" ", "-")}`,
    name, description: "", unit, quantity, libraryNames: [target],
  });
  return prepareBathroomTemplateScope(seeds, ctx).flatMap(seed => {
    if (/^Electrical (Work|Works|Installation)$/.test(seed.name)) {
      const sockets = number("socketCount"), lights = number("lightingCount");
      // Per-point rates need actual point counts, not a lump-sum quantity of 1.
      if (sockets != null || lights != null) {
        const points = (sockets ?? 0) + (lights ?? 0);
        return [
          line(seed, "First-Fix Electrics", "First fix electrics (per point)", "each", points),
          line(seed, "Second-Fix Electrics", "Second fix electrics (per point)", "each", points),
          line(seed, "Electrical Testing", "Electrical testing and certification", "item", 1),
        ];
      }
    }
    if (/^Plumbing (Works|Work|& WC)$/.test(seed.name)) {
      const sanitary = seeds.some(i => i.name === "Sanitaryware (Labour Only)");
      const points = ctx.projectType === "kitchen_refurb" ? 3
        : sanitary ? 2 + Number(seeds.some(i => i.name === "Shower (Labour Only)"))
          + Number(seeds.some(i => i.name === "Bath (Labour Only)")) : undefined;
      if (points != null) return [
        line(seed, "First-Fix Plumbing", "First fix plumbing (per point)", "each", points),
        line(seed, "Waste Connections", "First fix waste plumbing (per point)", "each", points),
      ];
    }
    // These two scopes used to combine unrelated units/rates in one line.
    if (seed.name === "Floor Insulation & Screed") return [
      line(seed, "Floor Insulation", "Garage conversion floor insulation", "sqm", seed.quantity),
      line(seed, "Floor Screed", "Sand and cement screed", "sqm", seed.quantity),
    ];
    if (seed.name === "Padstones & Lintels" && ctx.openings.length) return [
      line(seed, "Padstones", "Padstones", "each", seed.quantity),
      line(seed, "Lintels", "Catnic lintel install", "each",
        ctx.openings.reduce((n, o) => n + Number(o.quantity), 0)),
    ];
    if (seed.name === "Cavity Closers & Trays" && ctx.openings.length) return [
      line(seed, "Cavity Closers", "Cavity Closer Installation", "linear_m", seed.quantity),
      line(seed, "Cavity Trays", "Cavity Tray Installation", "each",
        ctx.openings.reduce((n, o) => n + Number(o.quantity), 0)),
    ];
    if (seed.name === "Sanitaryware (Labour Only)") return [
      // The full sanitaryware package also includes a bath/shower, already
      // separate selected template lines. Price only WC + basin here.
      line(seed, "WC Installation", "Install WC (close coupled)", "each", seed.quantity),
      line(seed, "Basin Installation", "Install basin and taps", "each", seed.quantity),
    ];
    if (seed.name === "Shower (Labour Only)") return [
      line(seed, "Shower Mixer", "Install shower mixer and rail", "each", seed.quantity),
      line(seed, "Shower Enclosure", "Install shower enclosure", "each", seed.quantity),
    ];
    let next: T = { ...seed, libraryNames: seed.libraryNames ?? REFERENCES[seed.name] };
    if (seed.name === "Plumbing Works") next.libraryNames = ["Plumbing Works"];
    if (seed.name === "Concrete Base" && floorArea != null) next = { ...next, unit: "sqm", quantity: floorArea };
    if (seed.name === "Strip Footings" && foundationRun != null) next.quantity = foundationRun;
    if (seed.name === "Making Good") {
      // A run of making-good work is one package, not N packages at a per-job rate.
      next = { ...next, unit: "item", quantity: seed.unit === "lm" ? 1 : seed.quantity };
    }
    if (/^Building Regulations (Application|Notice)$/.test(seed.name) || seed.name === "Permitted Development & Building Regulations Check") next.unit = "allow";
    if (/^Heating (Extension|Connection)$/.test(seed.name)) {
      next.unit = "each";
      next.quantity = number("radiatorCount") ?? seed.quantity;
    }
    const text = `${seed.name} ${seed.description}`.toLowerCase();
    if (seed.name === "Window Installation" && /\baluminium\b/.test(text) && !/\bupvc\b/.test(text))
      next.libraryNames = ["Supply and fit aluminium window"];
    if (seed.name === "Flooring" && /engineered/.test(text)) next.libraryNames = ["Engineered wood flooring"];
    if (seed.name === "Flooring" && /\blvt\b/.test(text) && !/laminate/.test(text)) next.libraryNames = ["Luxury vinyl tile (LVT)"];
    if (seed.name === "Worktop Installation (Labour Only)" && /stone|quartz/.test(text))
      next.libraryNames = ["Worktop installation (stone/quartz)"];
    // Known unit aliases, not dimension conversions. Never treat 1 item as 1 m².
    if (next.unit === "lm") next.unit = "linear_m";
    if (next.unit === "item" && /^(Skip Hire|Window Installation|Dormer Window|Internal Doors?|Internal Door at Loft Level|New Staircase|New Loft Staircase|Bath \(Labour Only\))$/.test(seed.name))
      next.unit = "each";
    return [next];
  });
}
